import { useSearchParams, Link } from 'react-router-dom'
import { useMemo } from 'react'
import { Building2, Link2, ArrowRight } from 'lucide-react'
import { industries, roles } from '../data/roles.ts'
import SeoH1 from '../components/SeoH1.tsx'

const calculatorMap: Record<string, { title: string; desc: string; path: string; color: string }> = {
  'ai-salary-gap': { title: 'AI Salary Gap', desc: 'Find your personal AI wage premium.', path: '/ai-salary-gap', color: 'bg-primary-50 text-primary-600' },
  'skills-roi': { title: 'AI Skills ROI', desc: 'Rank the highest-ROI skills for your role.', path: '/skills-roi', color: 'bg-emerald-50 text-emerald-600' },
  'displacement-risk': { title: 'AI Displacement Risk', desc: 'Reframe risk in dollars and upskilling actions.', path: '/displacement-risk', color: 'bg-rose-50 text-rose-600' },
  'ai-productivity': { title: 'AI Productivity Value', desc: 'Turn hours saved into annual dollar value.', path: '/ai-productivity', color: 'bg-amber-50 text-amber-600' },
  'employee-ai-reimbursement': { title: 'AI Expense Reimbursement', desc: 'Build the case for employer-paid AI tools.', path: '/employee-ai-reimbursement', color: 'bg-teal-50 text-teal-600' },
  'lifetime-earnings': { title: 'Lifetime Earnings', desc: 'Career NPV with and without AI upskilling.', path: '/lifetime-earnings', color: 'bg-indigo-50 text-indigo-600' }
}

export default function EnterpriseReportPage() {
  const [params] = useSearchParams()
  const companyName = params.get('company') || 'Your Company'
  const teamSize = Number(params.get('size')) || 50
  const industryId = params.get('industry') || industries[0].id
  const roleId = params.get('role') || roles[0].id
  const message = params.get('message') || ''
  const calculatorIds = (params.get('calcs') || 'ai-salary-gap,skills-roi').split(',').filter(Boolean)

  const calculators = useMemo(() => {
    return calculatorIds.map(id => calculatorMap[id]).filter(Boolean)
  }, [calculatorIds])

  const industry = industries.find(i => i.id === industryId) || industries[0]
  const role = roles.find(r => r.id === roleId) || roles[0]
  const shareUrl = window.location.href

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl)
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="card p-8 mb-8 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center">
            <Building2 className="w-7 h-7 text-white" />
          </div>
          <div>
            <div className="text-sm text-slate-300 uppercase tracking-wide font-semibold">AI Readiness Report</div>
            <SeoH1 fallback={`${companyName} AI Readiness Report`} className="text-3xl font-bold" />
          </div>
        </div>

        <p className="text-lg text-slate-300 mb-6">
          {message || `Prepared for ${companyName}. Explore your personal AI wage premium, displacement risk, and the highest-ROI skills for ${role.title}s in ${industry.name}.`}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white/10 rounded-lg p-4">
            <div className="text-sm text-slate-400">Company</div>
            <div className="text-xl font-semibold">{companyName}</div>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <div className="text-sm text-slate-400">Team size</div>
            <div className="text-xl font-semibold">{teamSize.toLocaleString()}</div>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <div className="text-sm text-slate-400">Industry</div>
            <div className="text-xl font-semibold">{industry.name}</div>
          </div>
        </div>

        <button
          onClick={handleCopyLink}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-medium transition-colors"
        >
          <Link2 className="w-4 h-4" />
          Copy report link
        </button>
      </div>

      <div className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Calculators for your team</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {calculators.map(calc => (
            <Link
              key={calc.path}
              to={calc.path}
              className="card p-5 hover:border-primary-300 hover:bg-primary-50 transition-colors group"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg mb-3 ${calc.color}`}>
                    <ArrowRight className="w-5 h-5" />
                  </div>
                  <h3 className="font-semibold text-slate-900 group-hover:text-primary-700">{calc.title}</h3>
                  <p className="text-sm text-slate-600 mt-1">{calc.desc}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="card p-6 bg-slate-50">
        <h3 className="text-sm font-semibold text-slate-900 mb-2">Why this matters for {companyName}</h3>
        <p className="text-sm text-slate-700">
          AI skills are already creating a measurable wage premium across {industry.name}. Employees who use these tools
          strategically can increase their output, reduce repetitive work, and future-proof their careers. This report is
          private and anonymous - no inputs are stored or transmitted.
        </p>
      </div>

      <div className="mt-8 text-center">
        <Link to="/enterprise-report-builder" className="text-sm text-primary-600 hover:underline">
          Create your own enterprise report
        </Link>
      </div>
    </div>
  )
}
